import { useContext, useEffect, useMemo } from 'react';

import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';

// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { getThemeFromQueryParams } from '@confluence/theming-utils/entry-points/getThemeFromQueryParams.ts';
import { applyTheme } from '@confluence/theming-utils/entry-points/applyTheme.ts';
import {
	setupThemeInsideIframe,
	applyThemeInsideIframe,
} from '@confluence/theming-utils/entry-points/initializeIframeTheming.ts';
import { fg } from '@confluence/feature-gating';
import type { ThemeState } from '@atlaskit/tokens/theme-config';

const THEME_STATE_QUERY_PARAM = 'themeState';
/**
 * When the themeState passthrough prop not available but query param `themeState` is available, we resolve the theme from this query param
 * After the themeState passthrough prop becomes available again during SPA phase, the theme should be resolved by passthrough props
 * This is needed for rendering consistent features between SSR and SPA phases, as SSR phase cannot resolve passthrough props through `window.postMessage`
 * By default, if no theme is provided the Atlaskit design system apply no theming and render the original
 * Confluence styles
 *
 * To learn more design tokens: https://atlassian.design/components/tokens/code#api
 * E.g.
 * - `?embedded=true&themeState=light%3Alight%20dark%3Adark%20colorMode%3Adark` should set the EP theme to dark mode
 * - `?embedded=true&themeState=llight%3Alight%20dark%3Adark%20colorMode%3Alight` should set the EP theme to light mode
 */

export const usePageTheme = (): ThemeState | Partial<ThemeState> | null => {
	// Below are the EP theming props
	const { themeState: themeStateProp } = useReactProps_DO_NOT_USE() || {};
	const { getQueryParams } = useContext(RoutesContext);

	const themeStateQueryParam = useMemo(
		() => getSingleParam(getQueryParams(), THEME_STATE_QUERY_PARAM),
		[getQueryParams],
	);

	const finalTheme = useMemo(
		() =>
			themeStateProp ||
			getThemeFromQueryParams({
				themeState: themeStateQueryParam,
			}),
		[themeStateProp, themeStateQueryParam],
	);

	useEffect(() => {
		// If a theme is requested and the global theme hasn't been set, set the global theme
		if (finalTheme) {
			void applyTheme(finalTheme);
		}
	}, [finalTheme]);

	// When running inside an iframe (e.g. native embed whiteboard), listen for
	// theme changes from the parent Confluence page and apply them locally.
	// This bridges the gap where the parent's theme mutation is not visible to
	// `useThemeObserver` running inside this iframe's document.
	useEffect(() => {
		if (!fg('confluence_pass_theme_change_to_iframes')) {
			return;
		}

		if (window === window.parent) {
			// Not inside an iframe – nothing to do.
			return;
		}

		try {
			// `setupThemeInsideIframe` calls `window.parent.initializeIframeTheming`
			// which is set up by the parent page via `addIframeThemingSupport()`.
			// It registers a ThemeMutationObserver on the parent document and
			// forwards theme updates to us via the provided callback.
			// The callback is invoked asynchronously (inside a .then()), so it
			// needs its own try/catch to avoid unhandled promise rejections.
			setupThemeInsideIframe((iframeThemeInfo) => {
				try {
					applyThemeInsideIframe(iframeThemeInfo, document);
				} catch {
					// applyThemeInsideIframe manipulates DOM — if it fails the
					// iframe keeps its current theme which is an acceptable fallback.
				}
			});
		} catch {
			// Expected in cross-origin iframes where window.parent properties
			// are inaccessible. Theme sync is not possible in that case and
			// the iframe will keep its initial theme from the URL query param.
		}
	}, []);

	return finalTheme;
};
